/**
 * Labstep
 *
 * @module grid/SmartTableProtocolValue/coldefs/protocol-value
 * @desc Grid SmartTable Column Definitions
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
} from 'ag-grid-community';
import { ResourceItemResourceLocationModal } from 'labstep-web/components/ResourceItem/ResourceLocationModal';
import { ICONS } from 'labstep-web/constants/icons';
import { EntityReadEntityContainer } from 'labstep-web/containers/Entity/Read/Entity';
import { ParamsStateContextProvider } from 'labstep-web/contexts/params-state';
import { suppressKeyboardEventClearCells } from 'labstep-web/core/DataGrid/CellEditor/utils';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import type {
  CellEditorParams,
  CellRendererParams,
  HeaderComponentParamsProps,
} from 'labstep-web/core/DataGrid/types';
import type { SmartTableColDef } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/types';
import { GridSmartTableColumnHeader } from 'labstep-web/grid/SmartTableProtocolValue/components/ColumnHeader';
import { GridSmartTableProtocolValueService } from 'labstep-web/grid/SmartTableProtocolValue/services/grid-smart-table-protocol-value.service';
import type { Experiment } from 'labstep-web/models/experiment.model';
import type { Protocol } from 'labstep-web/models/protocol.model';
import type { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import { ResourceItem } from 'labstep-web/models/resource-item.model';
import { ResourceLocation } from 'labstep-web/models/resource-location.model';

export const getProtocolValueResourceLocationColumnDef = (
  experimentOrProtocol: Experiment | Protocol,
): SmartTableColDef => {
  return {
    colId: `resource_location`,
    sortable: true,
    headerName: 'Location',
    headerComponent: GridSmartTableColumnHeader,
    headerComponentParams: (params: HeaderComponentParamsProps) => {
      return {
        toolPanelColumnName: `Location`,
        icon: ICONS.resource_location.primary,
        experimentOrProtocol,
      };
    },
    editable: (params: EditableCallbackParams) =>
      GridSmartTableProtocolValueService.getEditable(params),
    valueGetter: (params: ValueGetterParams) => {
      if (!params.node) {
        return '';
      }

      const protocolValue = params.data as ProtocolValue;

      return (
        protocolValue?.resourceItem?.resource_location?.guid || null
      );
    },
    valueSetter: (params) => {
      const newValue = params.newValue;
      if (
        !params.node ||
        newValue === '' ||
        params.newValue === params.oldValue
      ) {
        return false;
      }

      const protocolValue = params.data as ProtocolValue;

      if (protocolValue && protocolValue.resourceItem) {
        return GridSmartTableProtocolValueService.updateEntity(
          params,
          ResourceItem.entityName,
          protocolValue.resourceItem.idAttr,
          {
            resource_location_id: newValue,
          },
        );
      }

      return false;
    },
    cellRenderer: (params: CellRendererParams<ProtocolValue>) => {
      const protocolValue = params.data as ProtocolValue;

      if (!protocolValue || !protocolValue.resourceItem) {
        return null;
      }
      if (protocolValue.resourceItem.resource_location) {
        return (
          <EntityReadEntityContainer
            entityName={ResourceItem.entityName}
            id={protocolValue.resourceItem.id}
          >
            {({ entity: resourceItem }): JSX.Element => {
              return (
                <ParamsStateContextProvider value={{}}>
                  <ResourceItemResourceLocationModal
                    resourceItem={resourceItem}
                  />
                </ParamsStateContextProvider>
              );
            }}
          </EntityReadEntityContainer>
        );
      }

      return (
        <DataGridPlaceholder
          params={params}
          children={`Specify ${ResourceLocation.getHumanReadableEntityName()}`}
          editable={GridSmartTableProtocolValueService.getEditable(
            params,
          )}
          placeholder={`No ${ResourceLocation.getHumanReadableEntityName()}`}
        />
      );
    },
    cellEditor: (params: CellEditorParams<ProtocolValue>) => {
      if (!params.node) {
        return null;
      }

      const protocolValue = params.data as ProtocolValue;

      if (!protocolValue || !protocolValue.resourceItem) {
        return null;
      }
      return (
        <EntityReadEntityContainer
          entityName={ResourceItem.entityName}
          id={protocolValue.resourceItem.id}
        >
          {({ entity: resourceItem }): JSX.Element => {
            return (
              <ParamsStateContextProvider value={{}}>
                <ResourceItemResourceLocationModal
                  resourceItem={resourceItem}
                  hideMap
                  modalProps={{
                    open: true,
                    viewComponent: null,
                    onClose: (): void => {
                      params.api.stopEditing();
                      params.api.clearRangeSelection();

                      const rowIndex =
                        'rowIndex' in params ? params.rowIndex : null;
                      if (rowIndex !== null) {
                        params.api.setFocusedCell(
                          rowIndex + 1,
                          params.column,
                        );
                      }
                    },
                  }}
                />
              </ParamsStateContextProvider>
            );
          }}
        </EntityReadEntityContainer>
      );
    },
    // FIXME
    // cellStyle: (params) =>
    //   GridSmartTableProtocolValueService.getCellStyle(
    //     params,
    //     experimentOrProtocol,
    //     ProtocolValue.entityName,
    //     name,
    //   ),
    suppressKeyboardEvent: suppressKeyboardEventClearCells,
    valueClearer: () => {
      return null;
    },
  };
};
